import { Api } from "./Api";
import { Intervention } from "../models/Intervention";
import axios from "axios";
import { Employe } from "../models/Employe";
import _ from "lodash";
import moment from "moment";

export class InterventionApi extends Api {
    constructor() {
        super("intervention");
    }

    getAll(params?: Object): Promise<Api.CallResponse> {
        return this.getResources(params).then(function (response) {
            var res: Array<Intervention> = [];
            if (response.data && response.data.data) {
                res = _.map(response.data.data, function (d) {
                    return new Intervention(d);
                });
            }
            return {
                data: res,
                metadata: response.data.metadata,
                paginator: response.data.paginator,
            };
        });
    }

    get(id: Number, params?: Object): Promise<Intervention> {
        return this.getResource(id, params).then(function (response) {
            return new Intervention(response.data.data);
        });
    }

    getByHash(hash: string, params?: Object): Promise<Intervention> {
        return axios
            .get(this.apiUrl + "hash/" + hash, { params: params })
            .then(function (response) {
                return new Intervention(response.data.data);
            });
    }
    save(intervention: Intervention, params?: Object): Promise<Intervention> {
        return super
            .saveResource(intervention.serialize(), params)
            .then(function (response) {
                return new Intervention(response.data.data);
            });
    }

    delete(id: Number, raison: string): Promise<Intervention> {
        return super.deleteResource(id, raison).then(function (response) {
            return new Intervention(response.data.data);
        });
    }

    getTotalDurees(params?: Object): Promise<Object> {
        params = _.assign(params, { totalDureesOnly: true });
        return this.getResources(params).then(function (response) {
            let res = [];
            if (response.data) {
                res = response.data;
            }
            return res;
        });
    }

    getDisponibilites(start_date, end_date, params): Promise<any> {
        return axios
            .get(this.apiUrl + "disponibilite/" + start_date + "/" + end_date, {
                params: params,
            })
            .then(function (response) {
                return response.data;
            });
    }

    getEmployesDisponibilites(start_date, end_date, params): Promise<any> {
        return axios
            .get(
                this.apiUrl +
                    "employe/disponibilite/" +
                    start_date +
                    "/" +
                    end_date,
                { params: params }
            )
            .then(function (response) {
                return response.data;
            });
    }

    getAbsencesUnassigned(start_date, end_date, params): Promise<any> {
        return axios
            .get(
                this.apiUrl +
                    "employe/heures_absences/" +
                    start_date +
                    "/" +
                    end_date,
                { params: params }
            )
            .then(function (response) {
                return response.data;
            });
    }

    getAlerts(
        type: Intervention.EAlertType,
        count?: boolean
    ): Promise<number | Array<Object>> {
        let params = {
            type: type,
            count: count,
        };
        return axios
            .get(this.apiUrl + "alert", { params: params })
            .then(function (response) {
                if (count) {
                    if (response.data) {
                        return parseInt(response.data);
                    } else {
                        return 0;
                    }
                } else {
                    var res: Array<any> = [];
                    if (response.data && response.data.data) {
                        res = _.map(response.data.data, function (o) {
                            if (
                                type ==
                                Intervention.EAlertType.LateInterventions
                            ) {
                                return new Intervention(o);
                            } else if (
                                type == Intervention.EAlertType.LateFacturations
                            ) {
                                return o;
                            } else if (
                                type == Intervention.EAlertType.OfflineUsers
                            ) {
                                return new Employe(o);
                            }
                        });
                    }
                    return res;
                }
            });
    }

    updatePlanning(id: Number, params?: Object): Promise<Intervention> {
        return axios
            .get(this.apiUrl + id + "/update_planning", { params: params })
            .then(function (response) {
                return new Intervention(response.data.data);
            });
    }

    // Utils
    getPhotos(id: Number): Promise<Array<Object>> {
        return axios.get(this.apiUrl + id + "/photo").then(function (response) {
            return response.data.data;
        });
    }

    // URL signée et temporaire du planning : l'iframe la charge sans JWT, et
    // la visionneuse PDF garde le nom de fichier du Content-Disposition.
    getPlanningPdfSignedUrl(
        id: Number,
        startDate: Date,
        endDate: Date,
        debugMode: boolean
    ): Promise<string> {
        let params: any = {
            startDate: moment(startDate).format("YYYY-MM-DD"),
            endDate: moment(endDate).format("YYYY-MM-DD"),
        };
        if (debugMode) {
            params.debugMode = 1;
        }

        return axios
            .get(this.apiUrl + "pdf/planning/" + id + "/url", {
                params: params,
            })
            .then(function (response) {
                return response.data.data.url;
            });
    }

    getCalendrierPdfUrl(
        employeIds: Array<Number>,
        startDate: string,
        action: string = "I"
    ): string {
        return (
            this.apiUrl +
            "pdf/calendrier/" +
            action +
            "?employeIds=" +
            _.join(employeIds, ",") +
            "&startDate=" +
            startDate
        );
    }

    // URL signée et temporaire du récapitulatif d'heures : l'iframe de
    // l'aperçu la charge sans JWT (AW-45).
    getRecapitulatifHeuresPdfSignedUrl(
        employesIds: Array<Number>,
        startDate: string,
        endDate: string,
        monthly: boolean
    ): Promise<string> {
        let params: any = {
            employesIds: _.join(employesIds, ","),
            startDate: startDate,
            endDate: endDate,
        };
        if (monthly) {
            params.monthly = 1;
        }

        return axios
            .get(this.apiUrl + "pdf/recapitulatif_heures/url", {
                params: params,
            })
            .then(function (response) {
                return response.data.data.url;
            });
    }

    // URL signée et temporaire de l'export PDF de la liste, avec ses filtres
    // et son tri : le nouvel onglet l'ouvre sans JWT (AW-45).
    getInterventionsPdfSignedUrl(params: Object): Promise<string> {
        return axios
            .get(this.apiUrl + "pdf/url", { params: params })
            .then(function (response) {
                return response.data.data.url;
            });
    }

    // Carte des pointages, réservée aux comptes connectés : chargée avec le
    // JWT, renvoyée en URL d'objet (à libérer par URL.revokeObjectURL).
    getPointageImageObjectUrl(mapsImageUrl: string): Promise<string> {
        // Chemin seul : l'URL absolue du transformer peut porter un autre
        // schéma ou hôte que la page (requête bloquée en contenu mixte).
        let url = new URL(mapsImageUrl, window.location.href);

        return axios
            .get(url.pathname + url.search, { responseType: "blob" })
            .then(function (response) {
                return URL.createObjectURL(response.data);
            });
    }

    sendRecapitulatifHeuresPdf(
        email: string,
        startDate: string,
        endDate: string,
        monthly: boolean
    ) {
        let params = {
            recipient: email,
            startDate: startDate,
            endDate: endDate,
            monthly: monthly,
        };
        return axios
            .post(this.apiUrl + "pdf/recapitulatif_heures", params)
            .then(function (response) {
                return response;
            });
    }
}
